import React, { useState } from "react";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { Icon, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from "@mui/material";

import { Consortium } from "../../models/consortium/consortium";
import DialogDelete from "../controls/dialogs/DialogDelete";
import DialogConsortium from "../controls/dialogs/DialogConsortium";
import DialogConsortiumMembers from "../controls/dialogs/DialogConsortiumMembers";
import ProgressBar from "../controls/ProgressBar";
import ErrorMessage from "../controls/ErrorMessage";
import { consortiumService } from "../../services/consortium.service";
import { authService } from "../../services/auth.service";

const ConsortiumsActionMenu = ({
  params,
  onSubmitConsortium,
  onConsortiumsUpdate,
}: {
  params: GridRenderCellParams<Consortium>;
  onSubmitConsortium: (consortium: Consortium) => void;
  onConsortiumsUpdate: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string>("");

  const [open, setOpenDialogs] = useState({
    edit: false,
    members: false,
    delete: false,
  });

  React.useEffect(() => {
    setIsAdmin(authService.isAdmin());
    setUserRole(authService.getRole());
  }, []);

  const openDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
    handleCloseMenu();
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
    setSelectedId(null);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setSelectedId(id);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedId) return;

    try {
      setLoading(true);
      const response = await consortiumService.deleteConsortium(selectedId);

      if (response?.ok) {
        onConsortiumsUpdate();
      }
    }
    catch (err) {
      console.error("Error deleting consortium:", err);
      setError("Er is een fout opgetreden");
    }
    finally {
      setLoading(false);
      closeDialog("delete");
    }
  };

  const handleSubmit = async (consortium: Consortium) => {
    onSubmitConsortium(consortium);
    closeDialog("edit");
  };

  return (
    <>
      <IconButton onClick={(e) => handleOpenMenu(e, params.id as string)}>
        <Tooltip title="Acties" placement="right" arrow>
          <Icon>more_vert</Icon>
        </Tooltip>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {!isAdmin && (
          <MenuItem onClick={() => openDialog("members")}>
            <ListItemIcon>
              <Icon>groups</Icon>
            </ListItemIcon>
            Organisaties weergeven
          </MenuItem>
        )}

        {isAdmin && (
          <>
            <MenuItem onClick={() => openDialog("members")}>
              <ListItemIcon>
                <Icon>groups</Icon>
              </ListItemIcon>
              Organisatie beheren
            </MenuItem>
            <MenuItem onClick={() => openDialog("edit")}>
              <ListItemIcon>
                <Icon>edit</Icon>
              </ListItemIcon>
              Wijzigen
            </MenuItem>
            <MenuItem onClick={() => openDialog("delete")}>
              <ListItemIcon>
                <Icon>delete</Icon>
              </ListItemIcon>
              Verwijderen
            </MenuItem>
          </>
        )}
      </Menu>

      <DialogConsortium
        open={open.edit}
        selectedConsortiumId={selectedId}
        onClose={() => closeDialog("edit")}
        onSubmit={handleSubmit}
      />

      <DialogConsortiumMembers
        open={open.members}
        consortiumId={selectedId}
        onClose={() => closeDialog("members")}
        onUpdated={onConsortiumsUpdate}
        readOnly={!isAdmin}
        members={params.row.members}
      />

      {isAdmin && (
        <DialogDelete
          open={open.delete}
          name={params.row.name || undefined}
          onClose={() => closeDialog("delete")}
          onDelete={handleConfirmDelete}
        />
      )}

      {loading && <ProgressBar />}
      {error && <ErrorMessage message={error} />}
    </>
  );
};

export default ConsortiumsActionMenu;
